/**
 * Premier Partners — the band directly beneath the Home hero.
 *
 * Obsidian Pulse, applied to a utility strip: partner marks sit quiet and
 * desaturated so they never compete with the hero headline above them. A wall of
 * equals, scrolling continuously — every mark rendered at the same 68px optical
 * height so no partner outranks another by virtue of its own aspect ratio.
 *
 * To add a partner, drop the logo in `client/public/media/partners/` and add an
 * entry below. Use a light or white monochrome logo on a transparent background:
 * the marks sit on a near-black band, so a dark logo disappears and an opaque
 * background shows as a visible rectangle inside its seat.
 */
import { useState } from "react";
import { Link } from "wouter";
import { sponsors, type Sponsor } from "@/data/sponsors";

/**
 * The confirmed premier partners, in the order the approved mockup shows them.
 *
 * Each entry points at a lossless WebP under `client/public/media/partners/`.
 * These assets were missing from the repository for a long time — they lived only
 * in the managed workspace, so every logo 404'd in a clean checkout and in the
 * deployed build. They are committed now; keep them that way, or the band
 * degrades to wordmarks again on the next clean deploy.
 *
 * Seats size to their own mark and the track scrolls, so adding a partner needs
 * an entry here plus its asset — no layout change at any count.
 *
 * Each `href` is the partner's own confirmed domain, opened in a new tab so a
 * visitor reading the home page is not navigated away from it. Only add an href
 * you have been given: sending traffic to a guessed domain misrepresents the
 * partner, and an unlinked mark is better than a wrong one.
 */
const PREMIER_PARTNERS = sponsors;

/**
 * Campaign tags, keyed by sponsor slug.
 *
 * Kept here rather than on the Sponsor record because this is a fact about one
 * event, not about the partner: Tebeau Group sponsors the Lunar New Year gala,
 * and when that campaign ends the tag leaves with it while the partner stays.
 * Putting it in `sponsors.ts` would mean a seasonal badge outliving its season
 * on the sponsor's own page too.
 */
const CAMPAIGN_TAGS: Record<string, { label: string }> = {
  "tebeau-group": { label: "Event Sponsor" },
  "feng-shui-journey": { label: "Cohost" },
};


function PartnerMark({
  partner,
  duplicate = false,
}: {
  partner: Sponsor;
  /** Set on the cloned marquee track: presentational only, never focusable. */
  duplicate?: boolean;
}) {
  // A partner whose logo cannot be loaded keeps its seat and shows its name.
  //
  // This used to return null, which removed the card. That hid a real problem
  // twice over: with every logo missing the band collapsed to just its label and
  // looked designed that way, and a confirmed partner silently disappeared from
  // the page they were promised placement on. A wordmark is the honest
  // degradation — the seat is still theirs, and a missing asset is visible.
  const [failed, setFailed] = useState(false);

  const mark = failed ? (
    <span className="premier-partners__wordmark">{partner.name}</span>
  ) : (
    <img
      src={partner.logoSrc}
      alt={duplicate ? "" : partner.name}
      // Intrinsic size, so the seat can be laid out at the right width before
      // the file lands. CSS still drives the rendered box (height:68px,
      // width:auto); these only supply the aspect ratio.
      width={partner.logoWidth}
      height={partner.logoHeight}
      // The band sits directly under the hero, so these are in the first
      // viewport on the busiest page — eager, not lazy.
      loading="eager"
      decoding="async"
      // Eager, but behind the hero background: the marks are a supporting strip,
      // and the hero image is the LCP candidate that should get the bandwidth
      // first. Low priority here is what keeps ten small logos from competing
      // with it.
      fetchPriority="low"
      className="premier-partners__logo"
      onError={() => setFailed(true)}
    />
  );

  const tag = CAMPAIGN_TAGS[partner.slug];

  return (
    <li className={`premier-partners__item${tag ? " premier-partners__item--tagged" : ""}`}>
      <Link
        href={`/sponsors/${partner.slug}`}
        className="premier-partners__link"
        // The tag is part of what this seat says, so it belongs in the label a
        // screen reader hears rather than being left as decoration beside it.
        aria-label={tag ? `${partner.name}, ${tag.label}, on The CHI Alliance` : `Meet ${partner.name} on The CHI Alliance`}
        // The clone must not be reachable: two tab stops per partner, one of
        // them moving, is a keyboard trap dressed as a decoration.
        tabIndex={duplicate ? -1 : undefined}
      >
        {mark}
        {tag && <span className="premier-partners__campaign-note" aria-hidden="true">{tag.label}</span>}
      </Link>
    </li>
  );
}


export default function PremierPartners() {
  return (
    <section className="premier-partners" aria-labelledby="premier-partners-label">
      <div className="premier-partners__inner">
        <h2 className="premier-partners__label" id="premier-partners-label">
          <span>Premier</span>
          <span className="premier-partners__label--accent">Partners</span>
        </h2>

        {/*
          A continuous marquee.

          The track is rendered twice and translated by exactly half the pair's
          width, which is what makes the seam invisible: at -50% the copy sits
          precisely where the original started. The duplicate is aria-hidden and
          removed from the tab order so assistive technology and keyboard users
          meet each partner once.
        */}
        <div className="premier-partners__viewport">
          <div className="premier-partners__marquee">
            <ul className="premier-partners__track">
              {PREMIER_PARTNERS.map(partner => (
                <PartnerMark key={partner.name} partner={partner} />
              ))}
            </ul>
            <ul className="premier-partners__track" aria-hidden="true">
              {PREMIER_PARTNERS.map(partner => (
                <PartnerMark key={`${partner.name}-duplicate`} partner={partner} duplicate />
              ))}
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        /* Height comes from the shared token the hero subtracts from the viewport,
           so the pair adds up to exactly one screen. box-sizing keeps the borders
           inside that budget instead of pushing the band 2px past it. */
        /* Flush against the hero: no margin, and no border-top of its own — the hero
           already draws the hairline between them, and two would read as a double
           rule. Flush is also what the shared band-height token assumes: hero plus
           band is exactly one viewport only when nothing sits between them. */
        .premier-partners { position:relative; box-sizing:border-box; height:var(--chi-partners-band); background:#0A0F1E; border-bottom:1px solid rgba(255,255,255,.07); }
        .premier-partners__inner { display:flex; box-sizing:border-box; height:100%; align-items:center; gap:clamp(1.5rem,3.5vw,3.25rem); max-width:80rem; margin:0 auto; padding:0 clamp(1rem,4vw,2rem); }

        /* Two lines, the second in amber: the band names itself the way the hero
           names the brand, so the strip reads as part of the hero rather than as
           the first row of the page body. */
        .premier-partners__label { flex-shrink:0; margin:0; font-family:'DM Serif Display',Georgia,serif; font-size:clamp(1.5rem,2.8vw,2.1rem); font-weight:400; line-height:.98; letter-spacing:-.01em; color:#FFF; }
        .premier-partners__label > span { display:block; }
        .premier-partners__label--accent { color:#F5A623; }

        /* Clips the track and softens both ends, so marks enter and leave the
           band rather than appearing and vanishing at a hard edge. */
        .premier-partners__viewport { flex:1; min-width:0; overflow:hidden; -webkit-mask-image:linear-gradient(90deg,transparent,#000 4rem,#000 calc(100% - 4rem),transparent); mask-image:linear-gradient(90deg,transparent,#000 4rem,#000 calc(100% - 4rem),transparent); }
        .premier-partners__marquee { display:flex; width:max-content; }
        .premier-partners__track { display:flex; flex-shrink:0; align-items:stretch; gap:clamp(.6rem,1.4vw,1.15rem); margin:0; padding:0 0 0 clamp(.6rem,1.4vw,1.15rem); list-style:none; animation:premier-partners-scroll 42s linear infinite; }

        /* Half the pair's width: the clone lands exactly where the first began. */
        @keyframes premier-partners-scroll { from { transform:translateX(0); } to { transform:translateX(-100%); } }

        /* Equal by height, not by width: every mark is 68px tall, and its seat is
           as wide as that mark needs. Forcing a common width instead would either
           clip the widest logo or strand the narrowest in empty space. */
        /* Sized to its mark: no fixed basis, because a fixed one would either
           clip a wide logo or leave a narrow one adrift in empty space. The track
           scrolls, so seats are not competing for room in a fixed row. */
        /* Positioned so the campaign note at the foot of a tagged seat measures
           itself against this seat, and lands in the seat's own bottom padding.
           Without it the note escapes to the marquee track and every note in the
           band stacks on one spot in the middle of the row. */
        .premier-partners__item { position:relative; display:flex; flex:0 0 auto; align-items:center; justify-content:center; padding:clamp(1rem,1.8vw,1.4rem) clamp(1.25rem,2.4vw,2rem); border:1px solid rgba(255,255,255,.1); border-radius:.6rem; background:rgba(255,255,255,.02); transition:border-color 240ms ease, background-color 240ms ease; }
        .premier-partners__item:hover { border-color:rgba(255,255,255,.22); background:rgba(255,255,255,.045); }

        /* Fixed 68px height (15% below the original 80px), with width derived
           from the mark's own aspect ratio. No max-width: a cap would shrink
           the widest mark below 68px and break
           the one guarantee here, that every logo is optically the same height.
           The seat sizes to the mark instead of the other way round. */
        .premier-partners__logo { width:auto; height:68px; object-fit:contain; opacity:.92; transition:opacity 240ms ease; }
        .premier-partners__item:hover .premier-partners__logo { opacity:1; }

        /* Stands in for a logo that failed to load. Set to the same optical
           weight as the marks beside it so a wordmark reads as a peer in the
           wall rather than as an error state. */
        .premier-partners__wordmark { display:block; overflow:hidden; max-width:100%; color:#E2E8F0; font-family:'Space Grotesk','DM Sans',sans-serif; font-size:clamp(.82rem,1.35vw,1.02rem); font-weight:700; letter-spacing:-.01em; line-height:clamp(1.5rem,2.4vw,2.05rem); text-align:center; text-overflow:ellipsis; white-space:nowrap; opacity:.92; transition:opacity 240ms ease; }
        .premier-partners__item:hover .premier-partners__wordmark { opacity:1; }
        /* Fills the seat so the whole tile is the target, not just the glyph —
           a logo with generous internal whitespace is otherwise a small, fiddly
           hit area sitting inside a large obvious-looking button. */
        /* Event imagery drifting behind the band, under black.

           The marks are the content here, so the picture is pushed a long way
           back: low opacity beneath a heavy black wash, and a drift slow enough
           to register as atmosphere rather than as something moving. Both layers
           sit under the content and take no pointer events, so nothing about the
           band's behaviour changes. */
        .premier-partners { position:relative; isolation:isolate; overflow:hidden; }
        .premier-partners::before,
        .premier-partners::after { content:""; position:absolute; inset:0; pointer-events:none; }
        .premier-partners::before {
          z-index:-2;
          background-image:url("/media/lny2027/gala-ballroom.webp");
          /* Framed on the crowd, not on the wall. At 38% the band was cropped to
             the upper wall, where the only thing in frame was the flat goat gobo
             projected on it: a vector shape pretending to be a photograph. The
             guests are in the lower half of the plate. */
          background-size:cover; background-position:center 74%;
          opacity:.3; animation:premierPartnersDrift 46s ease-in-out infinite alternate; will-change:transform; }
        .premier-partners::after {
          z-index:-1;
          background:linear-gradient(180deg,rgba(4,6,12,.9) 0%,rgba(6,8,16,.8) 45%,rgba(4,6,12,.92) 100%); }
        @keyframes premierPartnersDrift {
          0% { transform:scale(1.06) translate3d(-1.2%,0,0); }
          100% { transform:scale(1.14) translate3d(1.2%,-1%,0); } }

        .premier-partners__link { display:flex; align-items:center; justify-content:center; width:100%; height:100%; border-radius:.35rem; }
        /* An event sponsor's seat is marked by the seat, not by a label on it.

           The badge that used to sit here said the same thing twice — the words
           and then the gold — and crowded a 68px mark to do it. The gradient is
           the Fire Goat's own range, gold falling into crimson, so the tile reads
           as belonging to this campaign without a word of explanation. Two
           backgrounds, one clipped to the padding box and one to the border box,
           is what gives a gradient border without a wrapper element.

           The sponsorship still reaches a screen reader through the link's
           accessible name: colour alone is never the only carrier of meaning. */
        .premier-partners__item--tagged {
          border-color:transparent;
          background:
            linear-gradient(158deg,#7A0D1C,#3C0810) padding-box,
            linear-gradient(135deg,#FFF1B8 0%,#F0D56A 26%,#D4AF37 48%,#C8102E 78%,#9B0B1C 100%) border-box;
          box-shadow:0 0 18px rgba(212,175,55,.16), inset 0 1px 0 rgba(255,241,184,.2); }
        .premier-partners__item--tagged:hover {
          background:
            linear-gradient(158deg,#94111F,#4C0A15) padding-box,
            linear-gradient(135deg,#FFF8D2 0%,#F6E27A 26%,#E8C547 48%,#E0203C 78%,#B20D22 100%) border-box;
          box-shadow:0 0 26px rgba(232,197,71,.3), inset 0 1px 0 rgba(255,241,184,.32); }
        .premier-partners__item--tagged .premier-partners__logo { opacity:1; }
        /* Plain words at the foot of the seat — no pill, no rule, nothing to
           compete with the mark above it. Absolute, because the logo has to stay
           on the same line as every other logo in the band; putting this in flow
           would lift Tebeau Group's mark off that line. Hidden from assistive
           technology since the link's own name already says it, and hearing it
           twice is worse than hearing it once. */
        .premier-partners__campaign-note { position:absolute; left:0; right:0; bottom:.1rem; text-align:center;
          white-space:nowrap;
          font-family:'Space Grotesk',sans-serif; font-size:.88rem; font-weight:600;
          letter-spacing:.13em; text-transform:uppercase; line-height:1; pointer-events:none;
          /* Held back by colour rather than by opacity. Opacity put the span on
             its own transparency layer, and on a marquee that is already being
             composited at fractional offsets that is what turned the glyphs to
             mush. A dimmer gold reads just as quietly and stays sharp. */
          color:#C9A227; transition:color 240ms ease; }
        .premier-partners__item--tagged:hover .premier-partners__campaign-note { color:#F6E27A; }
        .premier-partners__link:focus-visible { outline:2px solid #E91E8C; outline-offset:6px; }


        /* Stacked label and track need more room than the desktop band, so the
           token grows with it — the hero subtracts the same value and the pair
           still sums to one screen. */
        @media (max-width:840px) {
          .premier-partners { height:auto; min-height:var(--chi-partners-band); }
          .premier-partners__inner { flex-direction:column; height:auto; align-items:flex-start; gap:1rem; padding:clamp(1.25rem,4vw,1.75rem) clamp(1rem,4vw,2rem); }
          .premier-partners__viewport { width:100%; }
          .premier-partners__item { flex:0 0 8.5rem; }
        }

        /* Motion off means off: the track stops and becomes a scrollable row the
           reader controls, rather than a paused animation they cannot advance.
           The clone is hidden so nothing repeats. */
        @media (prefers-reduced-motion:reduce) {
          .premier-partners__item,.premier-partners__logo,.premier-partners__wordmark { transition:none; }
          .premier-partners::before { animation:none; transform:scale(1.06); }
          .premier-partners__item--tagged { transition:none; }
          .premier-partners__viewport { overflow-x:auto; -webkit-mask-image:none; mask-image:none; }
          .premier-partners__track { animation:none; }
          .premier-partners__track[aria-hidden="true"] { display:none; }
        }
      `}</style>
    </section>
  );
}
